import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { useParams } from 'react-router';
import { useApolloClient } from '@apollo/client';
import { IntegrationV2Query, IntegrationTypeV2 } from '@/gql/graphql';
import { getEventsList, getUserSelectedEvents } from '@/routes/trackings/tracking-utils';
import { useEffect } from 'react';
import { SaveButton } from '../../components/save-button';

import {
  FormLayout,
  FormSection,
  PasswordField,
  InputField
} from './../../components/tracking-details-form/base';
import {
  ConsentModeSection,
  FormHeaderSection,
  CustomEventsSection
} from '../../components/tracking-details-form/shared-sections';
import { useIntegrationStore } from '../../stores/tracking-store';

const formSchema = z.object({
  credentials: z.object({
    id: z.string().min(1, 'Dataset ID is required.'),
    accessToken: z.string().min(1, 'CAPI Access Token is required.')
  }),
  consentMode: z.boolean(),
  customEvents: z.array(z.string()).optional()
});

const eventsList = getEventsList(IntegrationTypeV2.Meta);

interface MetaCredentials {
  id: string;
  accessToken: string;
}

export const MetaSettingsForm = ({ onContinue }: { onContinue: () => void }) => {
  const { toast } = useToast();
  const params = useParams<{ id: string; slug: string }>();
  const client = useApolloClient();
  const { integration, isLoading, updateSettings } = useIntegrationStore();

  const isInitialSetup =
    !(integration?.credentials as MetaCredentials | undefined)?.id &&
    !(integration?.credentials as MetaCredentials | undefined)?.accessToken;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      credentials: {
        id: '',
        accessToken: ''
      },
      consentMode: false,
      customEvents: []
    }
  });

  useEffect(() => {
    if (!isLoading && integration) {
      const credentials = integration.credentials as MetaCredentials | undefined;

      form.reset({
        credentials: {
          id: credentials?.id ?? '',
          accessToken: credentials?.accessToken ?? ''
        },
        consentMode: integration.consentMode ?? false,
        customEvents: getUserSelectedEvents({
          integrationV2: integration as IntegrationV2Query['integrationV2']
        })
      });
    }
  }, [integration, form.reset]);

  const { isDirty } = form.formState;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      await updateSettings(client, {
        id: params.id!,
        customEvents: values.customEvents ?? [],
        consentMode: values.consentMode,
        credentials: values.credentials
      });
      toast({
        title: 'Form submitted',
        description: 'Your Meta integration settings have been saved.',
        variant: 'default'
      });
      onContinue();
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to save Meta integration settings.',
        variant: 'destructive'
      });
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(onSubmit)(e);
        }}
      >
        <FormLayout>
          <FormHeaderSection title="Meta Settings">
            In order to send transactions data to this destination, we need the following
            information from your Meta account.
          </FormHeaderSection>
          <FormSection columns={2}>
            <PasswordField
              form={form}
              name="credentials.accessToken"
              label="Conversions API Token"
            />
            <InputField
              form={form}
              name="credentials.id"
              label="Dataset ID"
              placeholder="e.g. 123456789012345"
            />
          </FormSection>
          <ConsentModeSection form={form} />
          <CustomEventsSection
            form={form}
            eventsList={eventsList}
            getUserSelectedEvents={() =>
              getUserSelectedEvents({
                integrationV2: integration as IntegrationV2Query['integrationV2']
              })
            }
          />
          <FormSection>
            <SaveButton isDirty={isDirty} isUpdating={isLoading} isInitialSetup={isInitialSetup} />
          </FormSection>
        </FormLayout>
      </form>
    </Form>
  );
};
